import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LinkOutlinedIcon from '@mui/icons-material/Link';

import type { SummaryReport } from '../../../types.ts';
import {
    buildShareOptions,
    describeUser,
    directoryName,
    groupLetter,
    loadShareCandidates,
    userName,
    type ShareCandidate,
} from '../shareCandidates.ts';
import {
    shareHeadingSx,
    shareLinkInputSx,
    sharePopperSx,
    shareSectionSx,
} from '../dialogStyles.ts';

interface ShareReportProps {
    report: SummaryReport | null;
    userId: string;
    isBusy: boolean;
    onShare: (userIds: string[]) => Promise<boolean>;
    onError: (message: string) => void;
}

export default function ShareReport({
    report,
    userId,
    isBusy,
    onShare,
    onError,
}: ShareReportProps) {
    const [candidates, setCandidates] = useState<ShareCandidate[]>([]);
    const [selection, setSelection] = useState<ShareCandidate[]>([]);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        loadShareCandidates()
            .then(setCandidates)
            .catch(() => onError('Could not load the list of people to share with.'));
    }, [onError]);

    const options = useMemo(
        () => buildShareOptions(candidates, [userId, ...(report?.sharedWithUserIds ?? [])]),
        [candidates, report, userId]
    );

    const shareLink = report
        ? `${window.location.origin}${window.location.pathname}?reportId=${report.id}`
        : '';

    const handleCopyLink = useCallback(() => {
        if (!shareLink) return;
        navigator.clipboard.writeText(shareLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [shareLink]);

    const handleShare = useCallback(async () => {
        const userIds = selection.map((user) => userName(user).trim()).filter(Boolean);
        if (!userIds.length) return;
        if (await onShare(userIds)) setSelection([]);
    }, [selection, onShare]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {report && (
                <Typography variant='body2' color='text.secondary'>
                    Sharing <strong>{report.name}</strong>
                </Typography>
            )}

            <Box sx={shareSectionSx}>
                <Box sx={shareHeadingSx}>
                    <LinkOutlinedIcon />
                    <Typography variant='subtitle2'>Copy Link</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                        size='small'
                        fullWidth
                        value={shareLink}
                        slotProps={{ input: { readOnly: true, sx: shareLinkInputSx } }}
                    />
                    <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                        <IconButton
                            onClick={handleCopyLink}
                            aria-label='copy link'
                            color={copied ? 'success' : 'default'}
                        >
                            {copied ? <CheckIcon /> : <ContentCopyIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mt: 1 }}
                >
                    The link opens this report for anyone it is shared with.
                </Typography>
            </Box>

            <Box sx={shareSectionSx}>
                <Box sx={shareHeadingSx}>
                    <GroupOutlinedIcon />
                    <Typography variant='subtitle2'>Share With Users</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Autocomplete
                        multiple
                        size='small'
                        sx={{ flex: 1 }}
                        options={options}
                        value={selection}
                        onChange={(_, selected) => setSelection(selected)}
                        getOptionLabel={describeUser}
                        getOptionKey={userName}
                        groupBy={groupLetter}
                        isOptionEqualToValue={(option, value) =>
                            userName(option) === userName(value)
                        }
                        noOptionsText='No one left to share with'
                        slotProps={{ paper: { sx: sharePopperSx } }}
                        renderOption={({ key, ...props }, user) => (
                            <Box
                                component='li'
                                key={key}
                                {...props}
                                aria-label={describeUser(user)}
                            >
                                <Stack
                                    sx={{
                                        minWidth: 0,
                                        padding: '10px 16px',
                                        borderBottom: '1px solid #eee',
                                    }}
                                >
                                    <Typography variant='body2' noWrap sx={{}}>
                                        {directoryName(user)}
                                    </Typography>
                                    <Typography variant='caption' color='text.secondary' noWrap>
                                        {userName(user)}
                                    </Typography>
                                </Stack>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder={selection.length ? '' : 'Search for people'}
                            />
                        )}
                    />
                    <Button
                        variant='contained'
                        size='small'
                        onClick={handleShare}
                        disabled={!selection.length || isBusy}
                    >
                        Share
                    </Button>
                </Box>
                {report && (report.sharedWithUserIds?.length ?? 0) > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant='caption' color='text.secondary'>
                            Already shared with
                        </Typography>
                        <Stack
                            direction='row'
                            flexWrap='wrap'
                            useFlexGap
                            sx={{ gap: 0.5, mt: 0.75 }}
                        >
                            {report.sharedWithUserIds?.map((recipient) => (
                                <Chip
                                    key={recipient}
                                    label={recipient}
                                    size='small'
                                    variant='outlined'
                                />
                            ))}
                        </Stack>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
